import Fuse, { FuseResult } from "fuse.js";
import { route } from "../../../../../../../shim/jsbridge";
import { LinkSearchRules, LLMSection, LLMTextLink } from "../@types/LLMSummary";
import {
  fuseConf,
  LINK_SEARCH_LIST,
  MAX_PROXIMITY,
  TOKEN_REGEX
} from "../consts";

interface Token {
  text: string;
  normalized: string;
  start: number;
  end: number;
}

const sourcePageBaseUrl = () => {
  const parts = window.location.href.split("/");
  const sourceIndex = parts.indexOf("source");
  const baseTarget = parts.slice(0, sourceIndex + 2).join("/");
  return baseTarget;
};

const tokenize = (paragraph: string): Token[] =>
  Array.from(paragraph.matchAll(TOKEN_REGEX)).map(match => ({
    text: match[0],
    normalized: match[0].replace(/[^\w]/g, "").toLowerCase(),
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length
  }));

const checkOverlapping = (existing: LLMTextLink, start: number, end: number) =>
  !(end < existing.start || start > existing.end);

const concatWithoutOverlaps = (
  links: LLMTextLink[],
  incomingLinks: LLMTextLink[]
): LLMTextLink[] => {
  const sorted = links.concat(incomingLinks).sort((a, b) => {
    if (a.text_idx !== b.text_idx) return a.text_idx - b.text_idx;
    if (a.start !== b.start) return a.start - b.start;
    return b.end - b.start - (a.end - a.start);
  });

  return sorted.reduce<LLMTextLink[]>((acc, current) => {
    const last = acc[acc.length - 1];
    const isOverlapping =
      last &&
      last.text_idx === current.text_idx &&
      checkOverlapping(last, current.start, current.end);

    if (!isOverlapping) {
      acc.push(current);
    }
    return acc;
  }, []);
};

const getLinkFromMatch = (
  mainMatch: FuseResult<string>,
  tokens: Token[],
  adjFuse: Fuse<string>,
  paragraph: string,
  paragraphIndex: number,
  ruleUrl: string
): LLMTextLink | null => {
  const anchorIdx = mainMatch.refIndex;
  const windowStart = Math.max(0, anchorIdx - MAX_PROXIMITY);
  const windowEnd = Math.min(tokens.length - 1, anchorIdx + MAX_PROXIMITY);

  let clusterStart = anchorIdx;
  let clusterEnd = anchorIdx;
  let matchFound = false;

  for (let i = windowStart; i <= windowEnd; i++) {
    if (i !== anchorIdx && adjFuse.search(tokens[i].normalized).length > 0) {
      clusterStart = Math.min(clusterStart, i);
      clusterEnd = Math.max(clusterEnd, i);
      matchFound = true;
    }
  }

  if (!matchFound) return null;

  const start = tokens[clusterStart].start;
  const end = tokens[clusterEnd].end;

  return {
    start,
    end: end - 1,
    text_idx: paragraphIndex,
    type: ruleUrl,
    value: paragraph.substring(start, end)
  };
};
export const buildPivotUrlForType = (pageUrl: string) =>
  `${sourcePageBaseUrl()}/${pageUrl}/`;

const URL_BUILDERS: Record<string, (match: string) => string | undefined> = {
  uri_ipv4: m => route("network_ip_summary", [m]),
  uri_ipv6: m => route("network_ip_summary", [m]),
  uri_url: m => route("network_url_summary", [m]),
  uri_domain: m => route("network_domain_summary", [m]),

  hash_sha1: m => `/redirect/${m}/`,
  hash_md5: m => `/redirect/${m}/`,
  hash_sha256: m => `/redirect/${m}/`,
  hash_sha512: m => `/redirect/${m}/`,

  uri_mail: m => route("search", [], { q: `email:${m}` }),
  hash_rha0: m => route("search", [], { q: `similar-to:${m}` }),
  hash_rha1: m => route("search", [], { q: `similar-to:${m}` }),
  hash_imphash: m => route("search", [], { q: `imphash:${m}` }),
  file_name: m => route("search", [], { q: `filename:"${m}"` }),
  file_type: m => route("search", [], { q: `filetype:${m}` }),
  threat_name: m => route("search", [], { q: `threatname:${m}` }),
  email_subject: m => route("search", [], { q: `email-subject:"${m}"` }),
  email_to: m => route("search", [], { q: `email-to:"${m}"` }),
  email_from: m => route("search", [], { q: `email-from:"${m}"` }),

  // Explicitly Unused
  yara_identifier: () => undefined,
  hash_tlsh: () => undefined,
  hash_ssdeep: () => undefined
};

export const buildUrlForType = (
  match: string,
  type: string
): string | undefined => {
  const builder = URL_BUILDERS[type];
  return builder ? builder(encodeURIComponent(match)) : undefined;
};

export const findPivotLinks = (
  data: LLMSection,
  linkSearchList: LinkSearchRules[] = LINK_SEARCH_LIST
) => {
  if (!data.text) return [];

  const allFoundLinks = data.text.flatMap((paragraph, paragraphIndex) => {
    const tokens = tokenize(paragraph);
    if (tokens.length === 0) return [];

    const fuseMain = new Fuse(
      tokens.map(t => t.text),
      fuseConf
    );

    return linkSearchList.flatMap(rule => {
      const mainMatches = fuseMain.search(rule.keyword);
      const adjFuse = rule.adjacentMatch;

      if (!adjFuse) return [];

      return mainMatches
        .map(mainMatch =>
          getLinkFromMatch(
            mainMatch,
            tokens,
            adjFuse,
            paragraph,
            paragraphIndex,
            rule.url
          )
        )
        .filter((link): link is LLMTextLink => !!link);
    });
  });

  return concatWithoutOverlaps(data.links ?? [], allFoundLinks);
};
